import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Box, Button, CircularProgress, Divider, Drawer, IconButton, InputAdornment, Stack, Table,
    TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { tokens, numberSx } from "../../theme";
import { openCircle, selectCircle } from "../../reducer/RawSurfaceSlice";
import { getFilterRejectReasons } from "./shaftRanges";

// Wide enough for two ~320px canvas panels plus gap and drawer padding (see the two-panel
// canvas row below) - the mockup Tommy and Marshall approved (Option A, side by side) needs
// meaningfully more room than the single-canvas view this replaces. Named rather than a bare
// literal, and capped by maxWidth rather than a MUI breakpoint, matching the convention
// RawSurfaceHistoryDialog.js's DIALOG_MAX_WIDTH already established for this app.
const DRILLDOWN_WIDTH = 720;

// Same wording/gestures as the main charts' own tooltip (RawSurfaceContent.js's PAN_ZOOM_HELP) -
// drag is the one difference worth calling out, since it pans here but selects a shaft range on
// the main charts (which need Space held to repurpose drag for panning instead).
const DRILLDOWN_PAN_ZOOM_HELP = (
    <>
        Scroll to zoom, or click and drag to pan. On a trackpad, pinch to zoom and swipe with two
        fingers to pan instead. Middle-click resets the view.
        <br /><br />
        Windows touchpad gestures vary by device/driver — if pinch-to-zoom doesn&apos;t do
        anything, hold Ctrl and scroll with two fingers instead.
    </>
);

// Zoom bounds for the circle canvases' scroll-wheel zoom, so it can't collapse to a point or
// run away to nothing on screen.
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 20;

/** Padded [min, max] for a set of values; a flat set gets a fixed 1-unit pad so it isn't a point. */
function niceDomain(values) {
    if (values.length === 0) return [-1, 1];
    const min = Math.min(...values);
    const max = Math.max(...values);
    if (min === max) return [min - 1, max + 1];
    const pad = (max - min) * 0.1;
    return [min - pad, max + pad];
}

/** Signed delta formatted with an explicit +/- sign, so "no change" (0.0) doesn't read as blank. */
const signedDelta = (value, digits = 1) => `${value >= 0 ? "+" : ""}${value.toFixed(digits)}`;

// The before/after comparison table's rows - this is the ONLY place these numbers are shown
// (an earlier version also had a separate "current fit" grid above it, but every field there
// duplicated a table row, so it was dropped). Center stays one combined row rather than
// splitting into separate Y/Z rows, since a straight-line shift is more meaningful than two
// independent axis deltas for judging "how far did the center move". Central angle is included
// because it genuinely can differ: fixing can drop the line's first or last surviving point,
// which shrinks the arc AngleBetweenPoints measures between.
const COMPARISON_FIELDS = [
    [
        "Center (Y, Z)",
        (c) => `${c.centerY.toFixed(1)}, ${c.centerZ.toFixed(1)} µm`,
        (o, f) => `${Math.hypot(f.centerY - o.centerY, f.centerZ - o.centerZ).toFixed(1)} µm`,
    ],
    ["Radius", (c) => `${c.radius.toFixed(1)} µm`, (o, f) => `${signedDelta(f.radius - o.radius)} µm`],
    ["Sigma", (c) => `${c.sigma.toFixed(1)} µm`, (o, f) => `${signedDelta(f.sigma - o.sigma)} µm`],
    [
        "Central angle",
        (c) => `${c.centralAngle.toFixed(1)}°`,
        (o, f) => `${signedDelta(f.centralAngle - o.centralAngle)}°`,
    ],
    ["Points", (c) => c.pointCount.toLocaleString(), (o, f) => signedDelta(f.pointCount - o.pointCount, 0)],
];

const legendLabelSx = { fontSize: "11.5px", color: tokens.muted };

/**
 * Index of the circle nearest a typed x, for the header's jump-to-X box. Snaps rather than
 * requiring an exact hit: circles only exist at XStart + n*XIncrement (50 µm apart in a typical
 * capture), so a hand-typed value landing exactly on one is the exception, not the rule - and
 * "jump to roughly here, then step with prev/next" is the actual investigative workflow this
 * serves (finding a specific cross-section by hover alone is what made it painful).
 *
 * Ties go to the lower x, so repeatedly committing a value exactly between two circles is stable
 * rather than oscillating. Returns -1 for an empty list.
 */
const nearestCircleIndex = (circles, x) => {
    let best = -1;
    let bestDistance = Infinity;
    for (let i = 0; i < circles.length; i++) {
        const distance = Math.abs(circles[i].x - x);
        if (distance < bestDistance) {
            best = i;
            bestDistance = distance;
        }
    }
    return best;
};

/**
 * Slide-over drilldown for a single fitted circle: the raw laser points for that line plotted
 * with the fitted circle overlaid, status, reject reasons, metrics, and prev/next navigation
 * through every circle in the capture (plus a type-an-X box in the header that snaps to the
 * nearest circle, since hover-hunting for one specific cross-section was the slow part of
 * comparing neighbouring lines by hand) - plus, per Tommy's request (mockup Option A, approved by
 * Tommy and Marshall), a side-by-side before/after comparison of the original Taubin fit against
 * the circle-fixed re-fit, independent of whichever of the two is presently "active" in the main
 * view's circleFixEnabled toggle.
 *
 * The gated summary (kept/dropReason/centre/radius/...) for whichever fit is presently active is
 * already local - it came down with the initial /fit or /filter response. The raw y/z points and
 * BOTH fits' full detail are fetched on open via openCircle/loadLinePoints (RawSurfaceSlice.js) -
 * see that thunk for why a per-circle round trip is needed here but not for anything else in this
 * tab, and for why it always requests the current circleFixLimit regardless of circleFixEnabled.
 *
 * Both canvases share ONE pan/zoom view (scroll to zoom, drag to pan, middle-click or the reset
 * button to return to the fit-to-data view) - per Marshall's request for graph zooming - rather
 * than independent per-panel views, since the whole point of the before/after layout is a shared
 * scale for an honest comparison; letting the two panels drift to different zoom levels would
 * quietly break that. Zoom is uniform on both axes (never per-axis, unlike GraphApp's own main
 * graphs) because this view's shape IS the data - an axis-locked zoom would stretch a circle into
 * an ellipse. Pan/zoom resets automatically when navigating to a different circle, but not when
 * this same circle's data merely refreshes (e.g. the circle-fix limit changed while the drawer is
 * open) - see the data effect below for how that distinction is made.
 */
export default function RawSurfaceDrilldown() {
    const rawSurface = useSelector((state) => state.rawSurface);
    const dispatch = useDispatch();
    const originalCanvasRef = useRef(null);
    const fixedCanvasRef = useRef(null);
    // MUI's Drawer mounts its children a render cycle after `open` itself flips true (it runs a
    // Slide transition internally), so the canvas refs above are still null at the moment a plain
    // `useEffect` keyed on our own "is a circle selected" boolean would run. Callback refs fire
    // exactly when React actually creates/destroys each DOM node, independent of that transition
    // timing, so this state - not a derived boolean - is what the listener-attaching effect below
    // depends on.
    const [canvasesReady, setCanvasesReady] = useState(false);
    // Stable (useCallback, empty deps) rather than created inline in JSX: an inline arrow function
    // passed as `ref` gets a new identity every render, which makes React detach and reattach it
    // (calling it with `null`, then the node again) on every single render, not just on real
    // mount/unmount - these two must stay referentially stable to only fire when the DOM node
    // itself actually changes.
    const setOriginalCanvasNode = useCallback((node) => {
        originalCanvasRef.current = node;
        setCanvasesReady(Boolean(originalCanvasRef.current) && Boolean(fixedCanvasRef.current));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const setFixedCanvasNode = useCallback((node) => {
        fixedCanvasRef.current = node;
        setCanvasesReady(Boolean(originalCanvasRef.current) && Boolean(fixedCanvasRef.current));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Pan/zoom view state and the domain it's applied on top of. Refs, not React state: the
    // interaction handlers below redraw directly on every wheel/mousemove event, and going
    // through setState/re-render for that would be both unnecessary and less smooth.
    const viewRef = useRef({ zoom: 1, panX: 0, panY: 0 });
    const domainRef = useRef({ yMid: 0, zMid: 0, span: 1 });
    const prevCircleXRef = useRef(undefined);
    const dragRef = useRef(null);
    // Safari-only pinch state (see the gesturestart/gesturechange handlers below) - mirrors
    // useChartPanZoom.js's own pinchStateRef, kept as a plain ref for the same reason as viewRef.
    const pinchStateRef = useRef(null);
    // Always holds the latest draw() so the mount-once event listeners below never call a
    // stale closure from whatever render they happened to be attached during.
    const drawRef = useRef(() => {});

    const circles = rawSurface.circles;
    const index = circles.findIndex((c) => c.x === rawSurface.selectedCircleX);
    const selected = index >= 0 ? circles[index] : null;

    // Same source as the main charts' tooltip and the Center Points Summary card - which filter
    // (NoiseReductionScatter/FocusRemoveWorst) rejected this circle during the last shim
    // calculation, independent of and in addition to circle-fix/angle/point/sigma gating below.
    const filterRejectReasons = useMemo(
        () => getFilterRejectReasons(rawSurface.filterTrace),
        [rawSurface.filterTrace]
    );
    const filterRejectReason = selected ? filterRejectReasons.get(selected.x) : undefined;

    const close = () => dispatch(selectCircle({ x: null }));
    const prev = () => index > 0 && dispatch(openCircle(circles[index - 1].x));
    const next = () => index < circles.length - 1 && dispatch(openCircle(circles[index + 1].x));

    // Draft text for the header's jump-to-X box, kept separate from selected.x so a partially
    // typed value ("185" on the way to "185750") isn't treated as a jump target on every
    // keystroke - navigation happens only on Enter/blur, via commitX below.
    const [xDraft, setXDraft] = useState("");

    // Re-sync the box whenever the drilldown lands on a different circle, from ANY source -
    // prev/next, a chart click, or commitX's own snap. Without this the box would keep showing
    // whatever was last typed while the view had already moved on.
    const selectedX = selected ? selected.x : null;
    useEffect(() => {
        setXDraft(selectedX === null ? "" : String(selectedX));
    }, [selectedX]);

    const commitX = () => {
        const typed = Number.parseFloat(xDraft.replace(/[\s,]/g, ""));
        if (!Number.isFinite(typed) || circles.length === 0) {
            // Unparseable (or nothing loaded): silently restore the box to where the view
            // actually is, rather than leaving invalid text sitting in a field that no longer
            // describes what's on screen.
            setXDraft(selected ? String(selected.x) : "");
            return;
        }

        const target = circles[nearestCircleIndex(circles, typed)];
        setXDraft(String(target.x));
        if (target.x !== rawSurface.selectedCircleX) {
            dispatch(openCircle(target.x));
        }
    };

    const onXKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            commitX();
            e.currentTarget.blur();
        } else if (e.key === "Escape") {
            // Abandon the edit without navigating. Stopping propagation matters: MUI's Drawer
            // closes on Escape, and abandoning a typo shouldn't also tear down the drilldown.
            e.preventDefault();
            e.stopPropagation();
            setXDraft(selected ? String(selected.x) : "");
            e.currentTarget.blur();
        }
    };

    const pointsReady = rawSurface.linePointsStatus === "ready" && rawSurface.linePoints?.x === selected?.x;
    const line = pointsReady ? rawSurface.linePoints : null;
    const original = line?.original ?? null;
    const fixed = line?.fixed ?? null;

    const resetView = () => {
        viewRef.current = { zoom: 1, panX: 0, panY: 0 };
        drawRef.current();
    };

    useEffect(() => {
        const originalCanvas = originalCanvasRef.current;
        const fixedCanvas = fixedCanvasRef.current;

        const draw = () => {
            if (!originalCanvas || !fixedCanvas || !selected) return;

            const points = line ?? { y: [], z: [], removed: [] };
            const y = points.y;
            const z = points.z;
            const removed = points.removed ?? [];
            const { yMid, zMid, span } = domainRef.current;
            const { zoom, panX, panY } = viewRef.current;

            const render = (canvas, mode) => {
                const rect = canvas.parentElement.getBoundingClientRect();
                const dpr = Math.max(1, window.devicePixelRatio || 1);
                canvas.width = Math.round(rect.width * dpr);
                canvas.height = Math.round(rect.height * dpr);
                const ctx = canvas.getContext("2d");
                ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
                const w = rect.width;
                const h = rect.height;
                ctx.clearRect(0, 0, w, h);

                const pad = 14;
                const baseScale = Math.min(w - pad * 2, h - pad * 2) / span;
                const scale = baseScale * zoom;
                const sy = (yy) => w / 2 + panX + (yy - yMid) * scale;
                const sz = (zz) => h / 2 + panY - (zz - zMid) * scale;

                const circle = mode === "original" ? original : fixed;
                if (circle?.hasFit) {
                    ctx.strokeStyle = mode === "original" ? tokens.muted : tokens.kept;
                    ctx.lineWidth = 1.5;
                    if (mode === "original") ctx.setLineDash([5, 4]);
                    ctx.beginPath();
                    const steps = 90;
                    for (let i = 0; i <= steps; i++) {
                        const ang = -Math.PI + (2 * Math.PI * i) / steps;
                        const yy = sy(circle.centerY + circle.radius * Math.cos(ang));
                        const zz = sz(circle.centerZ + circle.radius * Math.sin(ang));
                        if (i === 0) ctx.moveTo(yy, zz); else ctx.lineTo(yy, zz);
                    }
                    ctx.stroke();
                    ctx.setLineDash([]);

                    ctx.fillStyle = tokens.muted;
                    ctx.beginPath();
                    ctx.arc(sy(circle.centerY), sz(circle.centerZ), 2, 0, Math.PI * 2);
                    ctx.fill();
                }

                for (let i = 0; i < y.length; i++) {
                    // Only the "after" panel distinguishes dropped points - "before" is what
                    // production saw prior to fixing, where every point looks the same.
                    const dropped = mode === "fixed" && removed[i];
                    ctx.fillStyle = dropped ? tokens.reject : tokens.textSoft;
                    ctx.beginPath();
                    ctx.arc(sy(y[i]), sz(z[i]), dropped ? 2 : 1.6, 0, Math.PI * 2);
                    ctx.fill();
                }
            };

            render(originalCanvas, "original");
            render(fixedCanvas, "fixed");
        };

        drawRef.current = draw;

        if (!originalCanvas || !fixedCanvas || !selected) return;

        // Reset the shared view only when the OPEN CIRCLE actually changed - not on every data
        // refresh of the same circle. `rawSurface.circles` gets a brand-new array (and brand-new
        // circle objects) from applyFit on every threshold change, including a circle-fix-limit
        // change to the circle that's currently open, so `selected`'s object identity changes far
        // more often than the user actually navigates. Comparing the primitive `x` is what isolates
        // real navigation from a same-circle data refresh.
        if (selected.x !== prevCircleXRef.current) {
            viewRef.current = { zoom: 1, panX: 0, panY: 0 };
            prevCircleXRef.current = selected.x;
        }

        const points = line ?? { y: [], z: [], removed: [] };
        const allY = points.y.slice();
        const allZ = points.z.slice();
        [original, fixed].forEach((c) => {
            if (c?.hasFit) {
                allY.push(c.centerY - c.radius, c.centerY + c.radius);
                allZ.push(c.centerZ - c.radius, c.centerZ + c.radius);
            }
        });
        const [yMin, yMax] = niceDomain(allY);
        const [zMin, zMax] = niceDomain(allZ);
        domainRef.current = {
            yMid: (yMin + yMax) / 2,
            zMid: (zMin + zMax) / 2,
            span: Math.max(yMax - yMin, zMax - zMin) || 1,
        };

        draw();
    }, [selected, line, original, fixed]);

    // Pan/zoom interaction - attached once per drawer open (gated on canvasesReady, which callback
    // refs set the moment the DOM nodes actually exist - not on every circle navigation within an
    // already-open drawer, and not on every render). Reads/writes only refs, so a listener from an
    // earlier render is never stale in any way that matters.
    useEffect(() => {
        if (!canvasesReady) return undefined;
        const canvases = [originalCanvasRef.current, fixedCanvasRef.current].filter(Boolean);
        if (canvases.length === 0) return undefined;

        // Cursor-anchored zoom by a continuous factor (positive deltaY zooms out, negative in) -
        // shared by the wheel and gesture zoom paths below.
        const zoomByDelta = (rect, mx, my, deltaY) => {
            const { yMid, zMid, span } = domainRef.current;
            const { zoom, panX, panY } = viewRef.current;
            const pad = 14;
            const baseScale = Math.min(rect.width - pad * 2, rect.height - pad * 2) / span;
            const scale = baseScale * zoom;

            // Data-space point currently under the cursor, before the zoom changes.
            const dataY = yMid + (mx - rect.width / 2 - panX) / scale;
            const dataZ = zMid - (my - rect.height / 2 - panY) / scale;

            const factor = Math.exp(-deltaY * 0.0015);
            const newZoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoom * factor));
            const newScale = baseScale * newZoom;

            // Solve pan so that same data point stays under the cursor at the new zoom.
            const newPanX = mx - rect.width / 2 - (dataY - yMid) * newScale;
            const newPanY = my - rect.height / 2 + (dataZ - zMid) * newScale;

            viewRef.current = { zoom: newZoom, panX: newPanX, panY: newPanY };
        };

        // Zoom vs. pan is split by input. ctrlKey wheel always zooms (held-Ctrl scroll on any
        // mouse, or a trackpad pinch on Chromium/Firefox, which synthesize ctrlKey for exactly
        // this reason - unaffected by anything below). Otherwise, a plain mouse's scroll wheel
        // has no horizontal axis at all, so deltaX is always exactly 0 for one; a real trackpad
        // swipe, even one aimed straight up/down, is a two-finger gesture and in practice always
        // carries at least a tiny nonzero deltaX from natural hand movement. That's what tells
        // the two apart here: deltaX === 0 zooms (Kevin's preferred default for a plain mouse,
        // where zooming into a freshly-opened circle is usually the first thing you want to do,
        // and there's nothing yet to pan to), while an actual deltaX pans - preserving the
        // trackpad's own pinch-to-zoom/swipe-to-pan convention exactly as it already behaves
        // elsewhere, untouched by this preference.
        const handleWheel = (e) => {
            e.preventDefault();
            const rect = e.currentTarget.getBoundingClientRect();
            const mx = e.clientX - rect.left;
            const my = e.clientY - rect.top;

            if (e.ctrlKey || e.deltaX === 0) {
                zoomByDelta(rect, mx, my, e.deltaY);
            } else {
                // panX/panY are direct screen-pixel offsets here (unlike the main charts' data-
                // window bounds), so translating them by the wheel's own pixel deltas is a direct
                // 1:1 mapping - no data/pixel conversion needed, unlike useChartPanZoom.js's pan.
                viewRef.current = {
                    ...viewRef.current,
                    panX: viewRef.current.panX - e.deltaX,
                    panY: viewRef.current.panY - e.deltaY,
                };
            }
            drawRef.current();
        };

        // Safari doesn't synthesize ctrlKey on a wheel event during a trackpad pinch - it fires
        // its own proprietary gesturestart/gesturechange/gestureend events instead (WebKit-only;
        // Chromium/Firefox never fire these, so this is purely additive). e.scale is cumulative
        // since gesturestart, so the anchor point and starting zoom/pan are captured once at
        // gesturestart and every gesturechange recomputes from that fixed starting point.
        const handleGestureStart = (e) => {
            e.preventDefault();
            const rect = e.currentTarget.getBoundingClientRect();
            pinchStateRef.current = {
                mx: e.clientX - rect.left,
                my: e.clientY - rect.top,
                rect,
                startZoom: viewRef.current.zoom,
                startPanX: viewRef.current.panX,
                startPanY: viewRef.current.panY,
            };
        };

        const handleGestureChange = (e) => {
            e.preventDefault();
            const pinch = pinchStateRef.current;
            if (!pinch) return;
            const { mx, my, rect, startZoom, startPanX, startPanY } = pinch;
            const { yMid, zMid, span } = domainRef.current;
            const pad = 14;
            const baseScale = Math.min(rect.width - pad * 2, rect.height - pad * 2) / span;
            const startScale = baseScale * startZoom;

            const dataY = yMid + (mx - rect.width / 2 - startPanX) / startScale;
            const dataZ = zMid - (my - rect.height / 2 - startPanY) / startScale;

            const newZoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, startZoom * e.scale));
            const newScale = baseScale * newZoom;
            const newPanX = mx - rect.width / 2 - (dataY - yMid) * newScale;
            const newPanY = my - rect.height / 2 + (dataZ - zMid) * newScale;

            viewRef.current = { zoom: newZoom, panX: newPanX, panY: newPanY };
            drawRef.current();
        };

        const handleGestureEnd = (e) => {
            e.preventDefault();
            pinchStateRef.current = null;
        };

        const handleMouseDown = (e) => {
            if (e.button === 1) {
                // Middle-click resets, matching GraphApp's own convention.
                e.preventDefault();
                resetView();
                return;
            }
            if (e.button !== 0) return;
            dragRef.current = {
                startClientX: e.clientX,
                startClientY: e.clientY,
                startPanX: viewRef.current.panX,
                startPanY: viewRef.current.panY,
            };
            canvases.forEach((c) => { c.style.cursor = "grabbing"; });
        };

        const handleMouseMove = (e) => {
            if (!dragRef.current) return;
            const { startClientX, startClientY, startPanX, startPanY } = dragRef.current;
            viewRef.current = {
                ...viewRef.current,
                panX: startPanX + (e.clientX - startClientX),
                panY: startPanY + (e.clientY - startClientY),
            };
            drawRef.current();
        };

        const handleMouseUp = () => {
            if (!dragRef.current) return;
            dragRef.current = null;
            canvases.forEach((c) => { c.style.cursor = ""; });
        };

        canvases.forEach((c) => {
            c.addEventListener("wheel", handleWheel, { passive: false });
            c.addEventListener("gesturestart", handleGestureStart);
            c.addEventListener("gesturechange", handleGestureChange);
            c.addEventListener("gestureend", handleGestureEnd);
            c.addEventListener("mousedown", handleMouseDown);
        });
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);

        return () => {
            canvases.forEach((c) => {
                c.removeEventListener("wheel", handleWheel);
                c.removeEventListener("gesturestart", handleGestureStart);
                c.removeEventListener("gesturechange", handleGestureChange);
                c.removeEventListener("gestureend", handleGestureEnd);
                c.removeEventListener("mousedown", handleMouseDown);
            });
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canvasesReady]);

    const reasons = [
        ...(selected?.dropReason ? selected.dropReason.split("; ") : []),
        // Appended, not folded into dropReason itself - that field is the server's own gating
        // reason string; this is a separate, later-pipeline rejection this dialog now also
        // surfaces (matching the main charts' tooltip).
        ...(filterRejectReason ? [`Rejected by ${filterRejectReason}`] : []),
    ];
    const isRejected = !selected?.kept || !!filterRejectReason;

    const exportCsv = () => {
        if (!selected || !pointsReady) return;
        const { y, z, removed } = rawSurface.linePoints;
        const rows = [
            "y_microns,z_microns,removed",
            ...y.map((yv, i) => `${yv},${z[i]},${removed?.[i] ? "true" : "false"}`),
        ];
        const blob = new Blob([rows.join("\n")], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `circle-x${selected.x}-raw-points.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const canvasFrame = (ref, label, ariaLabel) => (
        <Box sx={{ flex: "1 1 0", minWidth: 0 }}>
            <Typography sx={{
                fontSize: "10.5px", color: tokens.muted, textTransform: "uppercase",
                letterSpacing: "0.04em", mb: "6px",
            }}>
                {label}
            </Typography>
            <Box sx={{
                position: "relative",
                width: "100%",
                height: "220px",
                backgroundColor: tokens.surface2,
                borderRadius: "8px",
            }}>
                <canvas
                    ref={ref}
                    role="img"
                    aria-label={ariaLabel}
                    style={{ width: "100%", height: "100%", display: "block", userSelect: "none", touchAction: "none" }}
                />
                {rawSurface.linePointsStatus === "loading" && (
                    <Box sx={{
                        position: "absolute", inset: 0, display: "flex", flexDirection: "column",
                        alignItems: "center", justifyContent: "center", gap: 1,
                    }}>
                        <CircularProgress size={22} thickness={3} color="secondary" />
                        <Typography variant="caption" color="text.secondary">
                            Loading raw points&hellip;
                        </Typography>
                    </Box>
                )}
                {rawSurface.linePointsStatus === "error" && (
                    <Box sx={{
                        position: "absolute", inset: 0, display: "flex",
                        alignItems: "center", justifyContent: "center", p: 2,
                    }}>
                        <Typography variant="caption" color="error" sx={{ textAlign: "center" }}>
                            {rawSurface.linePointsError}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );

    return (
        <Drawer
            anchor="right"
            open={selected !== null}
            onClose={close}
            PaperProps={{
                sx: {
                    width: DRILLDOWN_WIDTH,
                    maxWidth: "92vw",
                    backgroundColor: tokens.surface,
                    borderLeft: `1px solid ${tokens.line}`,
                    display: "flex",
                    flexDirection: "column",
                },
            }}
        >
            {selected && (
                <>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        sx={{ p: "14px 16px", borderBottom: `1px solid ${tokens.line}` }}
                    >
                        <Typography sx={{ fontWeight: 700, fontSize: "14px" }}>
                            Circle at X =
                        </Typography>
                        <Tooltip title="Type an X in µm and press Enter to jump to the nearest circle">
                            <TextField
                                value={xDraft}
                                onChange={(e) => setXDraft(e.target.value)}
                                onKeyDown={onXKeyDown}
                                onBlur={commitX}
                                onFocus={(e) => e.target.select()}
                                size="small"
                                variant="outlined"
                                inputMode="numeric"
                                aria-label="Jump to circle at X"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end" sx={{ ml: 0 }}>
                                            <Typography sx={{ fontSize: "12px", color: tokens.muted }}>µm</Typography>
                                        </InputAdornment>
                                    ),
                                    sx: { ...numberSx, fontSize: "13px", fontWeight: 700 },
                                }}
                                sx={{
                                    width: 118,
                                    "& .MuiOutlinedInput-input": { p: "4px 0 4px 8px", textAlign: "right" },
                                }}
                            />
                        </Tooltip>
                        <Box sx={{ flexGrow: 1 }} />
                        <IconButton size="small" onClick={prev} disabled={index <= 0} aria-label="Previous circle">
                            <ChevronLeftIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={next}
                            disabled={index >= circles.length - 1}
                            aria-label="Next circle"
                        >
                            <ChevronRightIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={close} aria-label="Close circle detail">
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Stack>

                    <Box sx={{ p: "16px", overflowY: "auto", flex: "1 1 auto" }}>
                        <Box sx={{ display: "flex", gap: "10px", mb: "10px" }}>
                            {canvasFrame(
                                setOriginalCanvasNode, "Original fit",
                                "Raw laser points for this circle with the original fitted circle overlaid"
                            )}
                            {canvasFrame(
                                setFixedCanvasNode, "After circle fixing",
                                "Raw laser points for this circle with the circle-fixed fit overlaid, dropped points marked"
                            )}
                        </Box>

                        <Stack direction="row" flexWrap="wrap" alignItems="center" columnGap={2} rowGap={0.5} sx={{ mb: "18px" }}>
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                                <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: tokens.textSoft }} />
                                <Typography sx={legendLabelSx}>Raw / kept point</Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                                <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: tokens.reject }} />
                                <Typography sx={legendLabelSx}>Dropped by circle fixing</Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                                <Box sx={{ width: 16, height: 0, borderTop: `2px dashed ${tokens.muted}` }} />
                                <Typography sx={legendLabelSx}>Original fit</Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                                <Box sx={{ width: 16, height: 0, borderTop: `2px solid ${tokens.kept}` }} />
                                <Typography sx={legendLabelSx}>Fixed fit</Typography>
                            </Stack>
                            <Tooltip title={DRILLDOWN_PAN_ZOOM_HELP} arrow placement="top">
                                <InfoOutlinedIcon sx={{ ml: "auto", fontSize: 15, color: tokens.muted, cursor: "help" }} />
                            </Tooltip>
                            <Tooltip title="Or middle-click a graph">
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="inherit"
                                    onClick={resetView}
                                    startIcon={<RestartAltIcon sx={{ fontSize: "16px !important" }} />}
                                    sx={{
                                        py: "2px",
                                        px: "10px",
                                        fontSize: "11.5px",
                                        lineHeight: 1.6,
                                        borderColor: tokens.line,
                                        color: tokens.textSoft,
                                        "&:hover": { borderColor: tokens.muted, backgroundColor: tokens.surface2 },
                                    }}
                                >
                                    Reset view
                                </Button>
                            </Tooltip>
                        </Stack>

                        <Box sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            fontSize: "11.5px",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            padding: "4px 9px",
                            borderRadius: "999px",
                            mb: "14px",
                            color: isRejected ? tokens.reject : tokens.kept,
                            backgroundColor: isRejected ? tokens.rejectFill : tokens.keptFill,
                        }}>
                            {isRejected ? "Rejected" : "Kept"}
                        </Box>

                        {isRejected && reasons.length > 0 && (
                            <Box sx={{
                                fontSize: "12px",
                                color: tokens.reject,
                                backgroundColor: tokens.rejectFill,
                                borderRadius: "8px",
                                padding: "10px 12px",
                                mb: 2,
                                display: "flex",
                                flexDirection: "column",
                                gap: "4px",
                            }}>
                                {reasons.map((r) => <span key={r}>&bull; {r}</span>)}
                            </Box>
                        )}

                        {original?.hasFit && (
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: "6px" }}>
                                    Circle fix at <span style={numberSx}>{line.micronLimit}</span>&nbsp;µm
                                </Typography>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ padding: "4px 8px", color: tokens.muted, fontSize: "11px" }}>
                                                Metric
                                            </TableCell>
                                            <TableCell align="right" sx={{ padding: "4px 8px", color: tokens.muted, fontSize: "11px" }}>
                                                Original
                                            </TableCell>
                                            <TableCell align="right" sx={{ padding: "4px 8px", color: tokens.muted, fontSize: "11px" }}>
                                                Fixed
                                            </TableCell>
                                            <TableCell align="right" sx={{ padding: "4px 8px", color: tokens.muted, fontSize: "11px" }}>
                                                &Delta;
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {COMPARISON_FIELDS.map(([label, format, delta]) => (
                                            <TableRow key={label}>
                                                <TableCell sx={{ padding: "5px 8px", fontSize: "12.5px" }}>{label}</TableCell>
                                                <TableCell align="right" sx={{ padding: "5px 8px", fontSize: "12.5px", ...numberSx }}>
                                                    {format(original)}
                                                </TableCell>
                                                <TableCell align="right" sx={{ padding: "5px 8px", fontSize: "12.5px", ...numberSx }}>
                                                    {fixed?.hasFit ? format(fixed) : "—"}
                                                </TableCell>
                                                <TableCell align="right" sx={{ padding: "5px 8px", fontSize: "12.5px", color: tokens.textSoft, ...numberSx }}>
                                                    {fixed?.hasFit ? delta(original, fixed) : "—"}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        )}

                        {/* Purely informational (always "Taubin") - kept below the table rather
                            than in its own grid row, since it's not a before/after value. */}
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: "10px" }}>
                            Fit method: Taubin
                        </Typography>
                    </Box>

                    <Divider sx={{ borderColor: tokens.line }} />
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ p: "12px 16px" }}>
                        <Typography variant="caption" color="text.secondary" sx={{ ...numberSx, flexGrow: 1 }}>
                            Circle {index + 1} / {circles.length}
                        </Typography>
                        <Button size="small" color="inherit" onClick={exportCsv} disabled={!pointsReady}>
                            Export CSV
                        </Button>
                    </Stack>
                </>
            )}
        </Drawer>
    );
}
